import React from 'react';
import { Package, DollarSign, Star, Eye, EyeOff, CheckCircle, XCircle, Image as ImageIcon } from 'lucide-react';

const ServicesTab = ({ vendorProfile }) => {
  const services = vendorProfile?.vendor_services || vendorProfile?.vendorServices || [];

  if (!services || services.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12">
        <Package className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Services Found</h3>
        <p className="text-sm text-gray-600">This vendor hasn't created any services yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
          Services ({services.length})
        </h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {services.map((service) => (
          <div
            key={service.id}
            className="bg-surface border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Service Image */}
            <div className="relative h-32 sm:h-40 bg-gray-100">
              {service.image_url ? (
                <img
                  src={service.image_url}
                  alt={service.service_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" />
                </div>
              )}
              
              {/* Status Badge */}
              <div className="absolute top-2 right-2">
                {service.is_active ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Inactive
                  </span>
                )}
              </div>

              {service.is_featured && (
                <div className="absolute top-2 left-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    Featured
                  </span>
                </div>
              )}
            </div>

            {/* Service Content */}
            <div className="p-3 sm:p-4">
              <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 line-clamp-2">
                {service.service_name}
              </h4>

              {service.description && (
                <p className="text-xs sm:text-sm text-gray-600 mb-3 line-clamp-2">
                  {service.description}
                </p>
              )}

              {/* Service Details */}
              <div className="space-y-2">
                {service.service_category && (
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <Package className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                    <span className="truncate">
                      {service.service_category.category_name || service.service_category?.name || 'N/A'}
                    </span>
                  </div>
                )}

                {service.base_price !== undefined && service.base_price !== null && (
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                    <span className="font-medium">
                      ${Number(service.base_price).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesTab;

